import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from 'services/apis/raster';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import InputAddress from 'v3/components/InputAddress';
import Button from 'v3/components/Button';
import RouteMachineMap from 'v3/components/Map/LeafletRouteMachine/Map';
import { useSnackbar } from 'v3/components/Snackbar';
import Text from 'v3/components/Text';
import Radio from 'v3/components/Radio';
import { FaTrash } from 'react-icons/fa';
import { useParams } from 'react-router-dom';
import { fetchCountries } from 'services/api/country';
import { Select } from 'v3/components';
import { formatAddress } from 'v3/utils/formatter';
import { useRoute } from './context';
import { fetchDistanceInKilometers } from './utils';
import { formatCurrency } from '../../../utils/formatter';
import { fetchSelectOptions } from './controller';
import StatusColor from './RouteStatus';
import polyline from '@mapbox/polyline';
import * as Styled from './styles';

const isValidCoordinate = (value) => typeof value === 'number' && !Number.isNaN(value);
const MAX_POLYLINE_INTERMEDIATE_WAYPOINTS = 1;

const buildViaPoint = (waypoint, index) => ({
  identifier: `via-${index}-${Number(waypoint.lat).toFixed(6)}-${Number(waypoint.lng).toFixed(6)}`,
  type: { id: 'via', name: 'Ponto intermediário' },
  action: { id: 'via', name: 'Ponto intermediário' },
  lat: waypoint.lat,
  lng: waypoint.lng,
  formatted_address: '',
  address: '',
});

function getBusinessPointIndexes(points = [], waypoints = []) {
  if (points.length < 2 || waypoints.length < points.length) {
    return points.map((_, index) => index);
  }

  const indexes = [0];
  let startIndex = 1;

  for (let pointIndex = 1; pointIndex < points.length - 1; pointIndex += 1) {
    const point = points[pointIndex];
    const remainingPoints = points.length - pointIndex - 1;
    const lastCandidateIndex = waypoints.length - remainingPoints - 1;
    let closestIndex = startIndex;
    let closestDistance = Number.POSITIVE_INFINITY;

    for (let waypointIndex = startIndex; waypointIndex <= lastCandidateIndex; waypointIndex += 1) {
      const waypoint = waypoints[waypointIndex];
      const latDelta = Number(point.lat) - Number(waypoint.lat);
      const lngDelta = Number(point.lng) - Number(waypoint.lng);
      const distance = (latDelta * latDelta) + (lngDelta * lngDelta);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = waypointIndex;
      }
    }

    indexes.push(closestIndex);
    startIndex = closestIndex + 1;
  }

  indexes.push(waypoints.length - 1);
  return indexes;
}

function mergeRouteWaypoints(points = [], waypoints = []) {
  const validWaypoints = (waypoints || []).filter(
    (point) => isValidCoordinate(point?.lat) && isValidCoordinate(point?.lng),
  );

  if (points.length < 2 || validWaypoints.length <= points.length) {
    return points;
  }

  const businessIndexes = getBusinessPointIndexes(points, validWaypoints);
  let businessPointIndex = 0;
  let viaPointIndex = 0;

  return validWaypoints.map((waypoint, waypointIndex) => {
    const isBusinessWaypoint = businessIndexes[businessPointIndex] === waypointIndex;

    if (isBusinessWaypoint) {
      const point = points[businessPointIndex];
      businessPointIndex += 1;
      return point;
    }

    const viaPoint = buildViaPoint(waypoint, viaPointIndex);
    viaPointIndex += 1;
    return viaPoint;
  });
}

function buildWaypointsFromPolyline(savedPolyline, businessPoints = []) {
  if (!savedPolyline || typeof savedPolyline !== 'string' || businessPoints.length < 2) {
    return [];
  }

  try {
    const decoded = polyline
      .decode(savedPolyline)
      .map(([lat, lng]) => ({ lat, lng }))
      .filter((point) => isValidCoordinate(point.lat) && isValidCoordinate(point.lng));

    if (decoded.length < 3) {
      return [];
    }

    const internalPoints = decoded.slice(1, -1);
    const sampleCount = Math.min(MAX_POLYLINE_INTERMEDIATE_WAYPOINTS, internalPoints.length);
    const sampled = [];

    for (let index = 1; index <= sampleCount; index += 1) {
      const sourceIndex = Math.round((index * (internalPoints.length - 1)) / (sampleCount + 1));
      sampled.push(internalPoints[sourceIndex]);
    }

    return [
      businessPoints[0],
      ...sampled,
      businessPoints[businessPoints.length - 1],
    ];
  } catch {
    return [];
  }
}

function formatDistanceFromMeters(distanceInMeters) {
  const distance = Number(distanceInMeters);
  if (!Number.isFinite(distance)) return null;

  return (distance / 1000).toFixed(1);
}

export default function Route() {
  const route = useRoute();
  const snackbar = useSnackbar();
  const inputsRef = useRef([]);
  const routeWaypointsSeededRef = useRef(false);
  const params = useParams();
  const [requestRasterRoute, setRequestRasterRoute] = useState(false);
  const [loadingRaster, setLoadingRaster] = useState(false);
  const [countryList, setCountryList] = useState([]);
  const [originCountrySelected, setOriginCountrySelected] = useState(null);
  const [destinationCountrySelected, setDestinationCountrySelected] = useState([]);
  const [originClient, setOriginClient] = useState(null);
  const [destinationClient, setDestinationClient] = useState([]);
  const [formattedOriginAddress, setFormattedOriginAddress] = useState('');
  const [formattedDestinationAddress, setFormattedDestinationAddress] = useState([]);
  const [routeWaypoints, setRouteWaypoints] = useState([]);
  const getPointLetter = (index) => String.fromCharCode(65 + index);

  const defaultAddress = {
    formatted_address: null,
    address: null,
    lat: null,
    lng: null,
    province: '',
    city: '',
    city_id: null,
    province_id: null,
    complement: '',
  };

  function getClientPrimaryAddress(client) {
    const addresses = client?.addresses || [];
    if (!addresses.length) {
      return null;
    }
    return addresses.find((address) => address?.current) || addresses[0];
  }

  function mapClientAddressToRoute(address, fallbackCountry) {
    if (!address) {
      return null;
    }

    const city = address?.city_name || address?.city?.name || address?.city || '';
    const province =
      address?.uf_name || address?.city?.province?.uf || address?.province || '';
    const countrySlug =
      address?.country?.abbreviation ||
      address?.country_slug ||
      address?.countrySlug ||
      fallbackCountry?.abbreviation ||
      fallbackCountry?.country_slug ||
      'br';
    const countryAbbreviation =
      typeof countrySlug === 'string' ? countrySlug.toLowerCase() : countrySlug;

    const lat = typeof address?.lat === 'number' ? address.lat : Number(address?.lat);
    const lng = typeof address?.lng === 'number' ? address.lng : Number(address?.lng);

    const formattedAddress =
      address?.formatted_address ||
      formatAddress({
        address: address?.address,
        number: address?.number,
        complement: address?.complement,
        neighborhood: address?.neighborhood,
        city,
        province,
        zip_code: address?.zip_code,
        country_slug: countrySlug,
      });

    return {
      ...defaultAddress,
      formatted_address: formattedAddress,
      address: address?.address || null,
      number: address?.number || null,
      complement: address?.complement || '',
      neighborhood: address?.neighborhood || null,
      zip_code: address?.zip_code || null,
      city,
      province,
      city_id: address?.city_id || null,
      province_id: address?.province_id || null,
      lat: Number.isFinite(lat) ? lat : null,
      lng: Number.isFinite(lng) ? lng : null,
      country_slug: countrySlug,
      country_abbreviation: countryAbbreviation || null,
    };
  }

  function objIsEmpty(obj) {
    if (!obj) {
      return obj;
    }

    const valid = Object.keys(obj).length == 0;

    if (valid) {
      return false;
    }

    return obj;
  }

  useEffect(() => {
    if (!params.id) {
      route.setData({
        destinations: [{ action: true }],
        origin: {},
        distance: 0,
      });
    }

    const destinations = [];

    if (route.data.destinations) {
      route.data.destinations.map((destination) => {
        destinations.push(formatAddress(destination));
      });
    }

    if (route.data.origin) {
      setFormattedOriginAddress(formatAddress(route.data?.origin));
    }

    setFormattedDestinationAddress(destinations);
    fetchCountries(setCountryList);
  }, []);

  useEffect(() => {
    if (countryList.length > 0) {
      const destinations = [];
      setOriginCountrySelected(
        countryList.find((country) => country?.abbreviation == route.data.origin?.country_slug),
      );
      route.data.destinations.map((destination) => {
        // isso vai manter a ordem correta dos destinos
        destinations.push(
          countryList.find((country) => country?.abbreviation == destination?.country_slug),
        );
      });

      setDestinationCountrySelected(destinations);
    }
  }, [countryList]);

  useEffect(() => {
    setOriginClient(route?.data?.origin?.client);
  }, [route?.data?.origin?.client]);

  useEffect(() => {
    if (!originClient) {
      return;
    }

    const address = getClientPrimaryAddress(originClient);
    const mappedAddress = mapClientAddressToRoute(address, originCountrySelected);
    if (!mappedAddress) {
      return;
    }

    if (!mappedAddress.lat || !mappedAddress.lng) {
      snackbar.show(
        <Text>
          Endereço do cliente de origem sem coordenadas. Selecione o endereço no
          autocomplete para gerar o ponto no mapa.
        </Text>,
        { type: 'warning' },
      );
    }

    route.setData({
      origin: {
        ...route.data.origin,
        ...mappedAddress,
        client_id: originClient?.id,
        client: originClient,
      },
    });

    setFormattedOriginAddress(mappedAddress.formatted_address || '');

    if (countryList.length > 0 && mappedAddress.country_slug) {
      const matchedCountry = countryList.find(
        (country) => country?.abbreviation == mappedAddress.country_slug,
      );
      if (matchedCountry && matchedCountry?.id !== originCountrySelected?.id) {
        setOriginCountrySelected(matchedCountry);
      }
    }
  }, [originClient]);

  useEffect(() => {
    if (params.id) {
      setDestinationClient(
        route?.data?.destinations?.map((destination) => {
          return destination?.client;
        }),
      );
    }
  }, [route?.data?.destinations]);

  function handleAddDestination() {
    route.setData({
      destinations: [...route.data?.destinations, { action: true }],
    });
  }

  function handleRemoveDestination(destinationIndex) {
    const filteredDestinations = route.data.destinations?.filter(
      (_, index) => index !== destinationIndex,
    );

    route.setData({
      destinations: filteredDestinations,
    });

    filteredDestinations.forEach((item, idx) => {
      if (!item?.address) {
        inputsRef.current[idx].refs.input.value = '';
      }
    });
  }

  const businessMapPoints = useMemo(() => {
    const points = [];
    if (route?.data?.origin?.lat && route?.data?.origin?.lng) {
      points.push({
        ...route.data.origin,
        type: { id: 'collect', name: 'Coleta' },
      });
    }

    if (route?.data?.destinations?.length > 0) {
      route.data.destinations.forEach((destination) => {
        if (destination?.lat && destination?.lng) {
          points.push({
            ...destination,
            type: {
              id: destination?.action ? 'delivery' : 'collect',
              name: destination?.action ? 'Descarga' : 'Carga',
            },
          });
        }
      });
    }

    return points;
  }, [route?.data?.origin, route?.data?.destinations]);

  const mapPoints = useMemo(
    () => mergeRouteWaypoints(businessMapPoints, routeWaypoints),
    [businessMapPoints, routeWaypoints],
  );

  const coordinatesKey = useMemo(() => {
    const coords = [];
    if (route?.data?.origin?.lat && route?.data?.origin?.lng) {
      coords.push(`${route.data.origin.lat},${route.data.origin.lng}`);
    }
    if (route?.data?.destinations?.length > 0) {
      route.data.destinations.forEach((destination) => {
        if (destination?.lat && destination?.lng) {
          coords.push(`${destination.lat},${destination.lng}`);
        }
      });
    }
    return coords.join('|');
  }, [route?.data?.origin, route?.data?.destinations]);

  useEffect(() => {
    const hasOrigin = !!(route?.data?.origin?.lat && route?.data?.origin?.lng);
    const hasDestination = (route?.data?.destinations || []).some(
      (d) => d?.lat && d?.lng,
    );

    routeWaypointsSeededRef.current = false;
    setRouteWaypoints([]);

    if (hasOrigin && hasDestination && !route?.data?.polyline) {
      fetchDistanceInKilometers(route);
    }
  }, [coordinatesKey]);

  useEffect(() => {
    if (!params.id || routeWaypointsSeededRef.current || routeWaypoints.length > 0) {
      return;
    }

    const waypoints = buildWaypointsFromPolyline(route?.data?.polyline, businessMapPoints);

    if (waypoints.length > 0) {
      routeWaypointsSeededRef.current = true;
      setRouteWaypoints(waypoints);
    }
  }, [businessMapPoints, params.id, route?.data?.polyline, routeWaypoints.length]);

  function handleChangeDestinationCountry(destinationIndex, country) {
    const destinations = [...route.data.destinations];
    destinations[destinationIndex] = {
      ...destinations[destinationIndex],
      country_slug: country?.country_slug,
      country_abbreviation:
        typeof country?.abbreviation === 'string'
          ? country.abbreviation.toLowerCase()
          : country?.country_slug || null,
      ...defaultAddress,
    };

    route.setData({
      destinations,
    });
  }

  function handleChangeDestinationAddress(destinationIndex, address) {
    const destinationAddress = [];
    const destinations = [...route.data.destinations];
    destinations[destinationIndex] = {
      ...destinations[destinationIndex],
      formatted_address: address?.formatted_address,
      ...defaultAddress,
    };
    destinationAddress.push(destinations[destinationIndex].formatted_address);
    setFormattedDestinationAddress(destinationAddress);

    route.setData({
      destinations,
    });
  }

  function setKMLPolyline(KML) {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(KML, 'application/xml');
    const coordinates = [...xmlDoc.getElementsByTagName('coordinates')];
    const allCoordinates = [];
    const chunk = [];
    coordinates.map((item) => {
      const aux = item.textContent.replace(/0\s/g, ' ');
      aux
        .split(',')
        .map((item) => item.trim())
        .filter((item) => item !== '0')
        .map((item) => allCoordinates.push(Number(item)));
    });
    for (let i = 0; i < allCoordinates.length; i += 2) {
      chunk.push(allCoordinates.slice(i, i + 2));
    }
    const processedChunk = chunk.map((item) => [item[0], item[1]]);
    const kmlPolyline = polyline.encode(processedChunk);

    route.setData({ polyline: kmlPolyline });
  }

  async function handleRasterCall() {
    if (!route?.data?.origin?.lat || !route?.data?.destinations[0]?.lat) {
      snackbar.show(<Text>Informe origem e destino para solicitar rota à RASTER!</Text>, {
        type: 'error',
      });
      return;
    }
    setRequestRasterRoute(true);
    setLoadingRaster(true);
    const tempLocations = [route?.data?.origin, ...route?.data?.destinations];
    const locations = tempLocations.map((location, index) => {
      if (index === 0 || index === tempLocations.length - 1) {
        return location.city;
      }
      return {
        Latitude: location.lat,
        Longitude: location.lng,
      };
    });
    try {
      const response = await api.post('/routes', { locations });
      route.setData({ id_rota_raster: response?.data[0]?.rasterId });
      const KMLUrl = response?.data[0]?.linkKml;
      const KMLContent = response?.data[0]?.kml;
      setKMLPolyline(KMLContent);
      const bennerResponse = response?.data[0]?.benner;

      snackbar.show(
        <Text>{`Rota atualizada de acordo com a definição da RASTER. ${
          !bennerResponse ? 'Rota não encontrada no Benner! Necessário incluir rota no Benner' : ''
        }`}</Text>,
        {
          type: 'warning',
        },
      );
    } catch (error) {
      setRequestRasterRoute(false);
      snackbar.show(<Text>Não foi possível puxar rota da RASTER. </Text>, {
        type: 'error',
      });
    } finally {
      setLoadingRaster(false);
    }
  }

  function hanldeDestinationClient(client, index) {
    const destinations = [...route.data.destinations];
    const address = getClientPrimaryAddress(client);
    const mappedAddress = mapClientAddressToRoute(address, destinationCountrySelected[index]);

    destinations[index] = {
      ...destinations[index],
      ...(mappedAddress || {}),
      client_id: client?.id,
      client,
    };

    if (mappedAddress && (!mappedAddress.lat || !mappedAddress.lng)) {
      snackbar.show(
        <Text>
          Endereço do cliente de destino sem coordenadas. Selecione o endereço
          no autocomplete para gerar o ponto no mapa.
        </Text>,
        { type: 'warning' },
      );
    }

    if (mappedAddress?.formatted_address) {
      setFormattedDestinationAddress((prev) => {
        const next = [...(prev || [])];
        next[index] = mappedAddress.formatted_address;
        return next;
      });
    }

    if (mappedAddress?.country_slug && countryList.length > 0) {
      const matchedCountry = countryList.find(
        (country) => country?.abbreviation == mappedAddress.country_slug,
      );
      if (matchedCountry) {
        setDestinationCountrySelected({
          ...destinationCountrySelected,
          [index]: matchedCountry,
        });
      }
    }

    route.setData({
      destinations,
    });
  }
  return (
    <Row>
      <Col xs={12} className="mb-3">
        <Styled.RoutePointCard
          style={{
            border: '1px solid #E5EAEE',
            borderRadius: 8,
            padding: '12px 14px',
            background: '#FAFCFE',
          }}
        >
          <div
            style={{
              borderBottom: '1px solid #E5EAEE',
              marginBottom: 12,
              paddingBottom: 8,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <span
              style={{
                minWidth: 24,
                height: 24,
                borderRadius: 12,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#E7F6EC',
                color: '#1F8F4E',
                fontWeight: 700,
                fontSize: 12,
              }}
            >
              {getPointLetter(0)}
            </span>
            <Text type="label" color="#1F2A37" weight={700}>
              Origem
            </Text>
          </div>

          <Row>
            <Col xs={12} className="mb-3">
              <Select.Async
                label="Cliente de Origem"
                value={objIsEmpty(originClient)}
                onSearch={(search) => fetchSelectOptions('persons/customers', { search })}
                getOptionLabel={(option) => `${option.social_name} - ${option.cgccpf}`}
                getOptionValue={(option) => option.id}
                useMenuPortal
                onChange={(event) => {
                  route.setData({
                    origin: {
                      ...route.data.origin,
                      client_id: event?.id,
                      client: event,
                    },
                  });

                  setOriginClient(event);
                }}
                error={route.errors?.['route.origin.client_id']}
              />
            </Col>

            <Col xs={12} className="mb-3">
              <Select
                label="País de Origem *"
                options={countryList}
                value={originCountrySelected}
                onChange={(country) => {
                  setOriginCountrySelected(country);

                  route.setData({
                    origin: {
                      ...route.data.origin,
                      country_slug: country?.country_slug,
                      country_abbreviation:
                        typeof country?.abbreviation === 'string'
                          ? country.abbreviation.toLowerCase()
                          : country?.country_slug || null,
                      ...defaultAddress,
                    },
                  });
                }}
                getOptionLabel={(option) => option.name}
                getOptionValue={(option) => option.id}
                error={route.errors[`route.origin.country_slug`]}
                useMenuPortal
              />
            </Col>

            <Col xs={12} className="mb-0">
              <InputAddress
                label="Origem *"
                type="text"
                placeholder=""
                value={formattedOriginAddress}
                types={[]}
                onChange={(event) => {
                  route.setData({
                    origin: {
                      ...route.data.origin,
                      formatted_address: event.target.value,
                      country_slug: originCountrySelected?.abbreviation,
                    },
                  });

                  setFormattedOriginAddress(event.target.value);
                }}
                country={originCountrySelected?.abbreviation?.toLowerCase() || `br`}
                onSelectedAddress={(address) => {
                  setFormattedOriginAddress(address.formatted_address);

                  const addressCountry = address?.country_abbreviation;
                  if (addressCountry && countryList.length > 0) {
                    const matchedCountry = countryList.find(
                      (country) => country?.abbreviation == addressCountry,
                    );
                    if (matchedCountry && matchedCountry?.id !== originCountrySelected?.id) {
                      setOriginCountrySelected(matchedCountry);
                    }
                  }

                  route.setData({
                    origin: {
                      ...address,
                      country_slug: addressCountry || originCountrySelected?.abbreviation,
                      country_abbreviation:
                        addressCountry ||
                        (typeof originCountrySelected?.abbreviation === 'string'
                          ? originCountrySelected.abbreviation.toLowerCase()
                          : originCountrySelected?.country_slug) ||
                        null,
                      client_id: originClient?.id,
                      client: originClient || {},
                    },
                  });
                }}
                error={route.errors['route.origin.city']}
                disabled={!originCountrySelected}
              />
            </Col>
          </Row>
        </Styled.RoutePointCard>
      </Col>

      {route.data?.destinations?.map((_, index) => {
        const isFinalDestination = route.data.destinations.length - 1 === index;

        return (
          <Col xs={12} className="mb-3" key={`destination-edit-${index}`}>
            <Styled.RoutePointCard
              style={{
                border: '1px solid #E5EAEE',
                borderRadius: 8,
                padding: '12px 14px',
                background: isFinalDestination ? '#FFF9F3' : '#FFFFFF',
              }}
            >
              <div
                style={{
                  borderBottom: '1px solid #E5EAEE',
                  marginBottom: 12,
                  paddingBottom: 8,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span
                    style={{
                      minWidth: 24,
                      height: 24,
                      borderRadius: 12,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: isFinalDestination ? '#FFF3E8' : '#EAF1FF',
                      color: isFinalDestination ? '#C8681A' : '#2E6FD8',
                      fontWeight: 700,
                      fontSize: 12,
                    }}
                  >
                    {getPointLetter(index + 1)}
                  </span>
                  <Text
                    type="label"
                    color={isFinalDestination ? '#8A5A00' : '#1F2A37'}
                    weight={700}
                  >
                    {isFinalDestination ? 'Destino Final' : `Destino ${index + 1}`}
                  </Text>
                </div>
                {isFinalDestination ? (
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={handleAddDestination}
                    style={{ minWidth: 160 }}
                  >
                    <Text type="regular" weight={500} fontSize="0.75rem" color="white">
                      + Adicionar destino
                    </Text>
                  </Button>
                ) : null}
              </div>

              <Row>
                <Col xs={12} className="mb-3">
                  <Select.Async
                    label={isFinalDestination ? 'Cliente de Destino' : `Cliente de Destino ${index + 1}`}
                    value={destinationClient[index]}
                    onSearch={(search) => fetchSelectOptions('persons/customers', { search })}
                    getOptionLabel={(option) => `${option.social_name} - ${option.cgccpf}`}
                    getOptionValue={(option) => option.id}
                    useMenuPortal
                    onChange={(event) => {
                      hanldeDestinationClient(event, index);
                      setDestinationClient([
                        ...destinationClient.slice(0, index),
                        event,
                        ...destinationClient.slice(index + 1),
                      ]);
                    }}
                    error={route.errors?.[`route.destinations[${index}].client_id`]}
                  />
                </Col>

                <Col xs={route.data.destinations.length - 1 !== index ? 10 : 12} className="mb-3">
                  <Select
                    label={
                      route.data.destinations.length - 1 === index
                        ? 'País de Destino *'
                        : `País de Destino ${index + 1} *`
                    }
                    options={countryList}
                    value={destinationCountrySelected[index]}
                    onChange={(country) => {
                      setDestinationCountrySelected({
                        ...destinationCountrySelected,
                        [index]: country,
                      });
                      handleChangeDestinationCountry(index, country);
                    }}
                    getOptionLabel={(option) => option.name}
                    getOptionValue={(option) => option.id}
                    error={route.errors[`route.destinations[${index}].country_slug`]}
                    useMenuPortal
                  />
                </Col>

                <Col xs={route.data.destinations.length - 1 !== index ? 10 : 12} className="mb-3">
                  <InputAddress
                    ref={(ref) => {
                      inputsRef.current[index] = ref;
                    }}
                    label={route.data.destinations.length - 1 === index ? 'Destino final *' : `Destino ${index + 1} *`}
                    type="text"
                    placeholder=""
                    value={formattedDestinationAddress[index]}
                    types={[]}
                    country={destinationCountrySelected[index]?.abbreviation?.toLowerCase() || `br`}
                    onChange={(event) => {
                      handleChangeDestinationAddress(index, event.target.value);
                    }}
                    onSelectedAddress={(address) => {
                      route.setData({
                        destinations: route?.data?.destinations.map((item, idx) => {
                          handleChangeDestinationAddress(index, address);
                          if (idx === index)
                            return {
                              ...item,
                              ...address,
                            };
                          return item;
                        }),
                      });
                    }}
                    error={
                      route.errors[`route.destinations[${index}].city`] ||
                      route.errors[`route.destinations[${index}].action`] ||
                      route.errors[`route.destinations[${index}].country`]
                    }
                    disabled={!destinationCountrySelected[index]}
                  />

                  {route.data.destinations.length - 1 !== index && (
                    <Radio.Group
                      onChange={(event) =>
                        route.setData({
                          destinations: route.data.destinations.map((item, idx) => {
                            if (idx === index)
                              return {
                                ...item,
                                action: event.target.value,
                              };
                            return item;
                          }),
                        })
                      }
                      value={route.data.destinations?.[index]?.action}
                      horizontal
                    >
                      <Radio value={false}>
                        <Text color="dark" type="label">
                          Carga
                        </Text>
                      </Radio>
                      <Radio value>
                        <Text color="dark" type="label">
                          Descarga
                        </Text>
                      </Radio>
                    </Radio.Group>
                  )}
                </Col>
                {route.data.destinations.length - 1 !== index && (
                  <Col xs={2} className="mb-3">
                    <Button
                      variant="error"
                      size="sm"
                      onClick={() => handleRemoveDestination(index)}
                      className="mt-4"
                    >
                      <FaTrash />
                    </Button>
                  </Col>
                )}
              </Row>
            </Styled.RoutePointCard>
          </Col>
        );
      })}
      <Col className="d-flex justify-content-between">
        {route?.data?.origin && route?.data?.destinations && (
          <span>
            <Text type="label" color="#464E5F">
              Distância (Km)
            </Text>
            <Text type="regular" color="gray" as="p" className="mt-2">
              {`${route?.data?.distance} Km` || 'Não informado'}
            </Text>
          </span>
        )}
        <Button
          onClick={handleRasterCall}
          variant="raster"
          style={{ border: '1px solid red' }}
          loading={loadingRaster}
        >
          Puxar rota da Raster
        </Button>
      </Col>
      {route.minimumFreightTableItem.value && route.data.distance > 0 && (
        <Col xs={12}>
          <Text type="label" color="#464E5F">
            Sugestão de valor de cobrança baseado na distância
          </Text>
          <Text type="regular" color="gray" as="p" className="mt-2">
            {formatCurrency(route?.minimumFreightTableItem?.value * Number(route?.data?.distance))}
          </Text>
        </Col>
      )}

      <Col xs={12}>
        <Text type="label" color="#464E5F">
          Mapa
        </Text>
        <Styled.RouteMapContainer>
          <RouteMachineMap
            points={mapPoints}
            canDragPoint={() => false}
            allowWaypointEditing
            readOnly={false}
            showReadOnlyOverlay={false}
            onWaypointsChange={(waypoints) => {
              setRouteWaypoints(waypoints);
            }}
            onRouteCalculated={({ encodedPolyline, route: calculatedRoute }) => {
              if (!encodedPolyline) {
                if (route?.data?.polyline) {
                  route.setData({ polyline: null });
                }
                return;
              }

              const distance = formatDistanceFromMeters(
                calculatedRoute?.summary?.totalDistance,
              );

              if (
                encodedPolyline !== route?.data?.polyline ||
                (distance && distance !== route?.data?.distance)
              ) {
                route.setData({
                  polyline: encodedPolyline,
                  ...(distance ? { distance } : {}),
                });
              }
            }}
          />
        </Styled.RouteMapContainer>
        <StatusColor />
      </Col>
    </Row>
  );
}
